import type { GameState, SeatPlayer, PlayerAction, PlayerActionType } from '@/types';
import { 
  calculateHandStrength, 
  calculatePotOdds, 
  calculatePositionAdvantage,
  calculatePreflopStrength
} from './hand-strength';

interface OpponentProfile {
  vpip: number; // Voluntarily Put $ In Pot
  pfr: number; // Pre-Flop Raise
  aggression: number;
  handsObserved: number;
  showdownWins: number;
}

export class HardAI {
  private opponentModels: Map<string, OpponentProfile> = new Map();
  private bluffFrequency: number = 0.15;
  private aggressionFactor: number = 0.8;

  decide(state: GameState, playerId: string): PlayerAction {
    const player = state.players.find(p => p.id === playerId);
    if (!player || !player.holeCards) {
      return { type: 'fold' };
    }

    const validActions = this.getValidActions(state, player);
    
    // 计算精确胜率
    const equity = state.communityCards.length === 0
      ? calculatePreflopStrength(player.holeCards)
      : calculateHandStrength(player.holeCards, state.communityCards);

    const potSize = state.pots.reduce((sum, p) => sum + p.amount, 0);
    const toCall = state.currentBet - player.currentBet;
    const potOdds = calculatePotOdds(potSize, toCall);
    const position = calculatePositionAdvantage(state, playerId);

    // 更新对手模型
    this.updateOpponentModels(state);

    // 获取人类玩家的模型
    const humanPlayer = state.players.find(p => p.isHuman);
    const opponentModel = humanPlayer ? this.opponentModels.get(humanPlayer.id) : undefined;

    // GTO 基础策略
    const gtoAction = this.getGTOAction(equity, potOdds, position, validActions);

    // 剥削性调整
    const exploitAction = opponentModel 
      ? this.getExploitAdjustment(gtoAction, opponentModel, equity, state, player)
      : gtoAction;

    // 混合策略：加入随机性
    return this.mixStrategy(exploitAction, equity, validActions, state, player);
  }

  private getGTOAction(
    equity: number,
    potOdds: number,
    position: number,
    validActions: PlayerActionType[]
  ): PlayerAction {
    // GTO 策略：基于期望值和位置
    const adjustedEquity = equity * (0.9 + position * 0.2);

    if (adjustedEquity > 0.75) {
      if (validActions.includes('raise')) {
        return { type: 'raise' };
      }
      return { type: 'all-in' };
    }

    if (adjustedEquity > 0.5) {
      if (potOdds < adjustedEquity && validActions.includes('call')) {
        return { type: 'call' };
      }
      if (validActions.includes('raise')) {
        return { type: 'raise' };
      }
    }

    if (adjustedEquity > 0.3) {
      if (potOdds < adjustedEquity * 0.8 && validActions.includes('call')) {
        return { type: 'call' };
      }
    }

    if (validActions.includes('check')) {
      return { type: 'check' };
    }

    return { type: 'fold' };
  }

  private getExploitAdjustment(
    baseAction: PlayerAction,
    opponentModel: OpponentProfile,
    equity: number,
    state: GameState,
    player: SeatPlayer
  ): PlayerAction {
    const validActions = this.getValidActions(state, player);
    const potSize = state.pots.reduce((sum, p) => sum + p.amount, 0);

    // 对手过于紧：可以多诈唬
    if (opponentModel.vpip < 0.25 && Math.random() < this.bluffFrequency * 1.5) {
      if (validActions.includes('raise') && equity < 0.4) {
        return { 
          type: 'raise', 
          amount: Math.min(potSize * 0.6, player.chips) 
        };
      }
    }

    // 对手过于松：价值下注更大
    if (opponentModel.vpip > 0.4 && equity > 0.6) {
      if (validActions.includes('raise')) {
        return { 
          type: 'raise', 
          amount: Math.min(potSize * 1.2, player.chips) 
        };
      }
    }

    // 对手过于激进：可以更多地跟注
    if (opponentModel.aggression > 0.7 && equity > 0.35) {
      if (validActions.includes('call')) {
        return { type: 'call' };
      }
    }

    return baseAction;
  }

  private mixStrategy(
    action: PlayerAction,
    equity: number,
    validActions: PlayerActionType[],
    state: GameState,
    player: SeatPlayer
  ): PlayerAction {
    const potSize = state.pots.reduce((sum, p) => sum + p.amount, 0);

    // 随机诈唬
    if (equity < 0.35 && Math.random() < this.bluffFrequency) {
      if (validActions.includes('raise')) {
        return { 
          type: 'raise', 
          amount: Math.min(Math.floor(potSize * 0.75), player.chips) 
        };
      }
    }

    // 强牌偶尔慢打
    if (equity > 0.8 && Math.random() < 0.1) {
      if (validActions.includes('check')) {
        return { type: 'check' };
      }
      if (validActions.includes('call')) {
        return { type: 'call' };
      }
    }

    // 处理加注金额
    if (action.type === 'raise') {
      const raiseAmount = this.calculateOptimalRaise(state, player, equity, potSize);
      return { type: 'raise', amount: raiseAmount };
    }

    return action;
  }

  private calculateOptimalRaise(
    state: GameState,
    player: SeatPlayer,
    equity: number,
    potSize: number
  ): number {
    const baseRaise = state.currentBet + state.minRaise;
    
    // 基于胜率和底池计算最优加注
    const optimalSize = potSize * (0.5 + equity * this.aggressionFactor);
    
    // 考虑筹码深度
    const stackToPot = player.chips / (potSize + 1);
    let finalRaise = Math.max(baseRaise, optimalSize);
    
    if (stackToPot < 5) {
      // 短筹码：更倾向于全押
      finalRaise = Math.min(finalRaise * 1.5, player.chips);
    }

    return Math.min(Math.floor(finalRaise), player.chips);
  }

  private updateOpponentModels(state: GameState): void {
    for (const player of state.players) {
      if (player.isHuman && !this.opponentModels.has(player.id)) {
        this.opponentModels.set(player.id, {
          vpip: 0,
          pfr: 0,
          aggression: 0,
          handsObserved: 0,
          showdownWins: 0,
        });
      }
    }
  }

  // 记录对手动作用于更新模型
  recordOpponentAction(playerId: string, action: PlayerAction, phase: string): void {
    const model = this.opponentModels.get(playerId);
    if (!model) return;

    model.handsObserved++;

    if (phase === 'preflop') {
      if (action.type === 'call' || action.type === 'raise' || action.type === 'all-in') {
        model.vpip = (model.vpip * (model.handsObserved - 1) + 1) / model.handsObserved;
      }
      if (action.type === 'raise' || action.type === 'all-in') {
        model.pfr = (model.pfr * (model.handsObserved - 1) + 1) / model.handsObserved;
      }
    }

    // 更新激进指数
    if (action.type === 'raise' || action.type === 'all-in') {
      model.aggression = (model.aggression * (model.handsObserved - 1) + 1) / model.handsObserved;
    } else if (action.type === 'call') {
      model.aggression = (model.aggression * (model.handsObserved - 1) + 0.5) / model.handsObserved;
    } else {
      model.aggression = (model.aggression * (model.handsObserved - 1) + 0) / model.handsObserved;
    }
  }

  private getValidActions(state: GameState, player: SeatPlayer): PlayerActionType[] {
    const actions: PlayerActionType[] = ['fold'];
    const toCall = state.currentBet - player.currentBet;

    if (toCall === 0) {
      actions.push('check');
    }

    if (player.chips > toCall) {
      if (toCall > 0) actions.push('call');
      actions.push('raise');
    }

    if (player.chips > 0) {
      actions.push('all-in');
    }

    return actions;
  }
}
